<template>
  <div>
    <v-card>
      <v-card-title>
        <v-title>Daily Usage Notification Logs</v-title>
      </v-card-title>

      <v-card-text>
        <v-layout row wrap align-end class="mb-2">
          <v-flex xs12 sm6 md3 mr-2>
            <label class="caption grey--text text--darken-1">Date range</label>
            <date-picker
              v-model="dateRange"
              lang="en-au"
              type="date"
              range
              value-type="YYYY-MM-DD"
              format="DD/MM/YYYY"
              placeholder="Select date range"
              style="width: 100%;"
              @change="onFiltersChanged"
            />
          </v-flex>

          <v-flex xs12 sm6 md3 mr-2>
            <v-text-field
              v-model="searchText"
              append-icon="search"
              label="Search"
              clearable
              hide-details
              @input="onSearchInput"
            />
          </v-flex>

          <v-flex xs12 sm6 md2 mr-2>
            <v-select
              v-model="channel"
              :items="channelOptions"
              label="Channel"
              clearable
              hide-details
              @change="onFiltersChanged"
            />
          </v-flex>

          <v-flex xs12 sm6 md2 mr-2>
            <v-select
              v-model="status"
              :items="statusOptions"
              label="Status"
              clearable
              hide-details
              @change="onFiltersChanged"
            />
          </v-flex>
        </v-layout>

        <v-data-table
          :headers="headers"
          :items="rowData"
          :total-items="total"
          :pagination.sync="pagination"
          :options.sync="options"
          :loading="loading"
          item-key="_rowKey"
        >
          <template slot="items" slot-scope="props">
            <td
              v-for="(prop, propIndex) in dataHeaders"
              :key="propIndex"
              :class="'text-xs-' + (prop.align || 'left')"
            >
              <span v-if="prop.date">{{ props.item[prop.value] | formatDate }}</span>
              <v-chip
                v-else-if="prop.value === 'channel'"
                v-bind="getChannelChipProps(props.item.channel)"
                label
                small
              >
                {{ props.item.channel }}
              </v-chip>
              <v-chip
                v-else-if="prop.value === 'status'"
                v-bind="getStatusChipProps(props.item.status)"
                label
                small
              >
                {{ props.item.status }}
              </v-chip>
              <span v-else-if="prop.truncate">
                {{ truncateText(props.item[prop.value], prop.truncate) }}
              </span>
              <span v-else>{{ props.item[prop.value] }}</span>
            </td>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { HTTP } from '../../http-common';

export default {
  name: 'DailyUsageNotificationLogs',
  data() {
    return {
      headers: [
        { text: 'Attempted At', value: 'attemptedAt', sortable: true, align: 'left', date: true },
        { text: 'Channel', value: 'channel', sortable: true, align: 'left' },
        { text: 'Status', value: 'status', sortable: true, align: 'left' },
        { text: 'Customer Number', value: 'customerNumber', sortable: true, align: 'left' },
        { text: 'CSN', value: 'csn', sortable: true, align: 'left' },
        { text: 'Recipient', value: 'recipient', sortable: true, align: 'left' },
        { text: 'Percent Used', value: 'percentUsed', sortable: true, align: 'right' },
        { text: 'Usage Bracket', value: 'usageBracket', sortable: true, align: 'left' },
        { text: 'Body', value: 'body', sortable: false, align: 'left', truncate: 80 },
        { text: 'Reason', value: 'reason', sortable: false, align: 'left', truncate: 80 },
      ],
      rowData: [],
      total: 0,
      pagination: {
        page: 1,
        rowsPerPage: 25,
        sortBy: 'attemptedAt',
        descending: true,
      },
      options: {},
      dateRange: null,
      searchText: '',
      channel: null,
      status: null,
      searchDebounceId: null,
      loading: false,
      channelOptions: ['SMS', 'Email'],
      statusOptions: ['Sent', 'Failed'],
    };
  },
  computed: {
    dataHeaders() {
      return this.headers;
    },
  },
  watch: {
    pagination: {
      handler() {
        this.fetchLogs();
      },
      deep: true,
    },
  },
  mounted() {
    this.fetchLogs();
  },
  methods: {
    truncateText(value, maxLen) {
      if (!value) return '';
      return value.length > maxLen ? `${value.substring(0, maxLen)}...` : value;
    },
    getChannelChipProps(channel) {
      switch (channel) {
        case 'Email':
          return { color: 'info', textColor: 'white' };
        case 'SMS':
          return { color: 'teal', textColor: 'white' };
        default:
          return { color: 'grey', textColor: 'white' };
      }
    },
    getStatusChipProps(status) {
      switch (status) {
        case 'Sent':
          return { color: 'success', textColor: 'white' };
        case 'Failed':
          return { color: 'error', textColor: 'white' };
        case 'Skipped':
          return { color: 'grey lighten-1', textColor: 'white' };
        default:
          return { color: 'grey', textColor: 'white' };
      }
    },
    onFiltersChanged() {
      this.pagination.page = 1;
      this.fetchLogs();
    },
    onSearchInput() {
      clearTimeout(this.searchDebounceId);
      this.searchDebounceId = setTimeout(() => {
        this.pagination.page = 1;
        this.fetchLogs();
      }, 300);
    },
    toPascalCase(value) {
      if (!value) return 'AttemptedAt';
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
    getRequestPayload() {
      return {
        dateFrom: this.dateRange && this.dateRange[0] ? this.dateRange[0] : null,
        dateTo: this.dateRange && this.dateRange[1] ? this.dateRange[1] : null,
        searchText: this.searchText || null,
        channel: this.channel || null,
        status: this.status || null,
        page: this.pagination.page,
        pageSize: this.pagination.rowsPerPage,
        sortBy: this.toPascalCase(this.pagination.sortBy),
        sortDesc: this.pagination.descending,
      };
    },
    async fetchLogs() {
      if (this.loading) return;
      this.loading = true;
      try {
        const response = await HTTP.post('OMS/ListDailyUsageNotificationLogs', this.getRequestPayload());
        const data = response.data || {};
        this.rowData = (data.items || []).map((item, index) => ({
          ...item,
          _rowKey: item.id != null ? String(item.id) : `${item.attemptedAt}-${item.csn}-${index}`,
        }));
        this.total = data.total || 0;
        const totalPages = Math.max(1, Math.ceil(this.total / this.pagination.rowsPerPage));
        if (this.pagination.page > totalPages) {
          this.pagination.page = totalPages;
        }
      } catch (error) {
        this.rowData = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
